import { createApp } from 'vue'
import App from './App.vue'
import router from './router'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import i18n from './i18n'
import { validateEnvironment } from './config/env'
import { getAuthService } from './msal/index'
import * as Sentry from '@sentry/vue'
import { useThemeStore } from './stores/theme'

// PrimeVue imports
import PrimeVue from 'primevue/config'
import AragoPreset from '@/assets/theme/arago-preset'
import ToastService from 'primevue/toastservice'
import ConfirmationService from 'primevue/confirmationservice'
import DialogService from 'primevue/dialogservice'
import Ripple from 'primevue/ripple'
import Tooltip from 'primevue/tooltip'

// Tailwind CSS v4 (doit être chargé en premier)
import '@/assets/styles/tailwind.css'

// Apollo Layout Styles
import '@/assets/layout/layout.scss'
import '@/assets/layout/arago-custom.scss'

// PrimeVue CSS & Thème Arago (après Tailwind pour éviter les conflits)
import 'primeicons/primeicons.css'
import '@/assets/theme/arago-theme.css'
import '@/assets/theme/dialog-custom.css'

// Toast Styles (UX améliorée)
import '@/assets/styles/toast.scss'

// Tooltip Styles (Affichage optimisé)
import '@/assets/styles/tooltip.scss'

// Components Modern Styles (Homogénéisation)
import '@/assets/styles/components-modern.scss'

// Design System Arago - Mixins SCSS et styles métier uniquement
// Les classes utilitaires sont fournies par Tailwind CSS v4
import '@/assets/theme/index.scss'

// import 'primeflex/primeflex.css' // PrimeFlex sera ajouté plus tard si nécessaire

// Axios plugin
import './plugins/axios'

// Vee-Validate
import './plugins/vee-validate'

// Styles globaux
// import './sass/overrides.sass' // Temporairement désactivé (contient des références Vuetify)

// Après un déploiement, un onglet resté ouvert référence des fichiers de l'ancien build qui
// n'existent plus : le chargement d'un écran échoue. On recharge alors la page une fois pour
// récupérer la nouvelle version (garde d'une minute contre une boucle de rechargement).
window.addEventListener('vite:preloadError', (event) => {
  const key = 'arago:preload-error-reload'
  try {
    const last = Number(sessionStorage.getItem(key) ?? 0)
    if (Date.now() - last < 60_000) return
    sessionStorage.setItem(key, String(Date.now()))
  } catch {
    return
  }
  event.preventDefault()
  window.location.reload()
})

// Fonction pour initialiser et monter l'application
async function initApp() {
  validateEnvironment()

  // 1. Initialiser MSAL EN PREMIER pour gérer le redirect avant Vue Router
  const authService = getAuthService()
  await authService.waitForInitialization()

  // 2. Créer l'application Vue seulement après MSAL
  const app = createApp(App)

  // 3. Initialiser Sentry (avant les autres plugins pour capturer toutes les erreurs)
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN
  const environment = import.meta.env.MODE || 'development'

  if (sentryDsn) {
    Sentry.init({
      app,
      dsn: sentryDsn,
      environment,
      integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],
      // Performance Monitoring - Réduit pour éviter les 429 Too Many Requests
      tracesSampleRate: environment === 'production' ? 0.05 : 0.1,
      // Session Replay - Réduit pour économiser le quota
      replaysSessionSampleRate: environment === "production" ? 0.01 : 0,
      replaysOnErrorSampleRate: environment === "production" ? 0.5 : 0.1,
      // Limite le nombre d'erreurs envoyées par session
      maxBreadcrumbs: 50,

      // Filtrer les erreurs non pertinentes
      ignoreErrors: [
        'ResizeObserver loop limit exceeded',
        'Non-Error promise rejection captured',
        'AADB2C90091', // User cancelled Azure B2C
        'AADB2C90118', // Reset password flow
        // Filtrer les erreurs PowerBI CustomEvent
        /^CustomEvent$/,
        /PowerBI.*error/i,
        'Erreur PowerBI',
      ],
      beforeSend(event) {
        // Bloquer en développement local pour éviter le spam
        if (environment === 'development' && window.location.hostname === 'localhost') {
          return null
        }

        // Bloquer les erreurs PowerBI déjà gérées par l'application
        if (
          event.exception?.values?.some(
            (v) =>
              v.type === 'CustomEvent' ||
              v.value?.includes('PowerBI') ||
              v.value?.includes('powerbi-client')
          )
        ) {
          console.log('🛡️ Sentry: Erreur PowerBI filtrée (déjà gérée par l\'application)')
          return null // Ne pas envoyer à Sentry
        }

        return event
      },
    })

    // Exposer Sentry en développement pour les tests manuels
    if (environment === 'development') {
      ;(window as Window & { Sentry?: typeof Sentry }).Sentry = Sentry
    }
  }

  // Configuration Pinia (remplace Vuex)
  const pinia = createPinia()
  pinia.use(piniaPluginPersistedstate)

  // Installation des plugins
  app.use(pinia)

  // Initialiser le thème (détection préférence système au premier lancement)
  const themeStore = useThemeStore()
  themeStore.init()

  app.use(router)
  app.use(i18n)

  // Configuration PrimeVue
  app.use(PrimeVue, {
    theme: {
      preset: AragoPreset,
      options: {
        darkModeSelector: '.app-dark', // Mode sombre activé
        cssLayer: false,
        prefix: 'p',
      },
    },
    ripple: true,
    locale: {
      accept: 'Oui',
      reject: 'Non',
      selectAll: 'Tout sélectionner',
      unselectAll: 'Tout désélectionner',
      // Traductions françaises PrimeVue
      startsWith: 'Commence par',
      contains: 'Contient',
      notContains: 'Ne contient pas',
      endsWith: 'Se termine par',
      equals: 'Égal à',
      notEquals: 'Différent de',
      noFilter: 'Aucun filtre',
      lt: 'Inférieur à',
      lte: 'Inférieur ou égal à',
      gt: 'Supérieur à',
      gte: 'Supérieur ou égal à',
      dateIs: 'Date égale à',
      dateIsNot: 'Date différente de',
      dateBefore: 'Date avant',
      dateAfter: 'Date après',
      clear: 'Effacer',
      apply: 'Appliquer',
      matchAll: 'Correspond à tout',
      matchAny: 'Correspond à au moins un',
      addRule: 'Ajouter une règle',
      removeRule: 'Supprimer la règle',
      choose: 'Choisir',
      upload: 'Télécharger',
      cancel: 'Annuler',
      dayNames: ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'],
      dayNamesShort: ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'],
      dayNamesMin: ['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa'],
      monthNames: ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'],
      monthNamesShort: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'],
      today: "Aujourd'hui",
      weekHeader: 'Sem',
      firstDayOfWeek: 1,
      dateFormat: 'dd/mm/yy',
      weak: 'Faible',
      medium: 'Moyen',
      strong: 'Fort',
      passwordPrompt: 'Saisir un mot de passe',
    }
  })

  app.use(ToastService)
  app.use(ConfirmationService)
  app.use(DialogService)

  // Directives
  app.directive('ripple', Ripple)
  app.directive('tooltip', Tooltip)

  // 3. Montage de l'application
  app.mount('#app')
}

// Lancer l'initialisation
initApp().catch(err => {
  console.error('Erreur initialisation application:', err)
  const app = createApp({
    data: () => ({
      message: err instanceof Error ? err.message : 'Erreur critique lors du demarrage de l\'application.',
    }),
    template: `
      <main style="min-height:100vh;display:flex;align-items:center;justify-content:center;padding:2rem;background:#f8fafc;color:#0f172a;font-family:Poppins,Segoe UI,Arial,sans-serif;">
        <section style="max-width:48rem;background:white;border-radius:1rem;padding:2rem;box-shadow:0 20px 45px rgba(15,23,42,0.12);">
          <h1 style="margin:0 0 1rem;font-size:1.5rem;color:#9f1239;">Initialisation interrompue</h1>
          <p style="margin:0;line-height:1.6;white-space:pre-line;">{{ message }}</p>
        </section>
      </main>
    `,
  })
  app.mount('#app')
})